import React, { useEffect, useMemo, useState } from "react";

// mui components
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableContainer,
	TableHead,
} from "@mui/material";

// custom components
import { TableCell, TableRow } from "@mui/material";
import Body2 from "../typography/body-02";

// custom header format
import TableHeadCheckboxSort from "./table-head/table-head-checkbox-sort";
import TableHeadConditionTracking from "./table-head/table-header-condition-tracking";
import TableHeadEnhancedConditionTracking from "./table-head/table-header-enhanced-condition-tracking";
import TableHeadSort from "./table-head/table-head-sort";
import CustomTableHead from "./table-head";
import TableHeadSatisfyCondition from "./table-head/table-header-satisfy-condition";
import TableHeadResourceFile from "./table-head/table-header-resource-file";
import TableHeadEligibleProduct from "./table-head/table-head-eligible-product";
import TableHeadIneligibleProduct from "./table-head/table-head-ineligible-product";
import TableHeadPipeline from "./table-head/table-head-pipeline";
import TableHeadLoanDocuments from "./table-head/table-head-loan-documents";
import TableHeadMessages from "./table-head/table-head-messages";
import TableHeadLoanDocumentsTracker from "./table-head/table-head-loan-documents-tracker";
import TableHeadDocumentTrackerConditions from "./table-head/table-header-document-tracker-conditions";

import { sortAmount, sortDate, sortNumber } from "../../utils/sort-methods";
import {
	getTrackerMatchingConditionInfo,
	getUploadedByDisplayText,
} from "./table-row/conditions-document-tracker";
import { getDocumentPipelineStatusPresentation } from "../../utils/document-pipeline-status";

// custom row format
import LoanStatusRow from "./table-row/loan-status-row";
import RateLockRow from "./table-row/rate-lock-row";
import PipelineRow from "./table-row/pipeline-row";
import LockHistoryRow from "./table-row/lock-history-row";
import ConditionTrackingRow from "./table-row/condition-tracking";
import EnhancedConditionTrackingRow from "./table-row/enhanced-condition-tracking";
import SatisfyingConditionsRow from "./table-row/satisfying-conditions";
import ChangeRequestRow from "./table-row/change-request";
import BrokerDocumentRow from "./table-row/broker-documents";
import LoanDocumentRow from "./table-row/loan-documents";
import AdditionalIncomeRow from "./table-row/additional-income";
import AssetInformationRow from "./table-row/asset-information";
import LiabilityInformationRow from "./table-row/liability-information";
import RealEstateInformationRow from "./table-row/real-estate-information";
import ResourceFolderRow from "./table-row/resource-folder-row";
import ResourceFileRow from "./table-row/resource-file-row";
import AdminUserRow from "./table-row/admin-user";
import LinkLiabilityRow from "./table-row/link-liability";
import AdminPipelineRow from "./table-row/admin-pipeline-row";
import AppraisalDocumentsRow from "./table-row/appraisal-documents-row";
import OtherAssetsInformationRow from "./table-row/other-assets-information";
import OtherLiabilitiesInformationRow from "./table-row/other-liabilities-information";
import EligibleProductRow from "./table-row/eligible-product";
import EligibleProductAdjustmentsRow from "./table-row/eligible-product-adjustments";
import MessageRow from "./table-row/message-row";
import EligibleProductPaymentsRow from "./table-row/eligible-product-payments";
import IneligibleProductRow from "./table-row/ineligible-product";
import ConditionsDocumentTrackerRow from "./table-row/conditions-document-tracker";
import DocumentTrackerConditionsRow from "./table-row/document-tracker-conditions";
// custom styles
import styles from "./index.module.scss";

const CustomTable = ({
	columnData,
	isSubmitted,
	rowData,
	tableType,
	squaredBorder,
	maxHeight,
	isLoading,
	enableSelect,
	enableSort,
	setSortedData,
	selectedRow,
	setSelectedRow,
	selectedDocumentIds,
	onToggleDocumentSelection,
	onToggleAllDocumentSelection,
	loanId,
	conditionTypeOptions,
	setConditionsOptions,
	isConditionStatusLoaded,
	selectedConditionTypes,
	setSelectedConditionTypes,
	conditionDocuments = [],
	onRefreshDocumentsList,
	setSelectedProduct,
	currentProductName,
	handleRemoveRow,
	handleUpdate,
	disableRow,
	// real estate
	liabilityCount,
	handleReOwned,
	handleViewLinked,
	// resources folder
	handleFolderClick,
	handleFolderDelete,
	// resources file
	handleFileRename,
	handleFileDelete,
	// admin user
	selectedBroker,
	handleSelectBroker,
	// link liability
	selectedRealEstate,
	handleSelectLiability,
	// condition tracking
	handleConditionUploadClick,
	handleConditionDownloadClick,
	setDocumentsList,
	// admin pipeline
	handleOpenConfirmModal,
	// appraisal documents
	handleDownload,
	// eligible pricing products
	toggleDrawer,
	extraData,
	selectedProduct,
	hidePriceColumn = false,
	hidePointsPercentageColumn = false,
	hidePointsValueColumn = false,
	hideAdjustmentPriceColumn = false,
	// messages
	onMessageRowClick,
	isAdmin,
	onViewDocument,
	onDownloadDocument,
}) => {
	// Helper function to render messages for admin/non-admin users
	const renderMessagesWithSections = () => {
		if (isAdmin) {
			// Admin users see normal table without sections
			return rowData?.map((row, index) => (
				<MessageRow
					row={row}
					key={index}
					onMessageRowClick={onMessageRowClick}
					isAdmin={isAdmin}
				/>
			));
		}

		const systemMessages =
			rowData?.filter((msg) => msg.message_type === "login_screen_alert") || [];

		// Non-admin users see messages grouped by read status
		const unreadMessages =
			rowData?.filter(
				(msg) => msg.message_type !== "login_screen_alert" && !msg.read
			) || [];
		const readMessages =
			rowData?.filter(
				(msg) => msg.message_type !== "login_screen_alert" && msg.read
			) || [];

		const renderedRows = [];

		if (systemMessages.length > 0) {
			systemMessages.forEach((row, index) => {
				renderedRows.push(
					<MessageRow
						row={row}
						key={`unread-${index}`}
						onMessageRowClick={onMessageRowClick}
						isAdmin={isAdmin}
					/>
				);
			});
		}

		// Unread section
		if (unreadMessages.length > 0) {
			// Section header
			renderedRows.push(
				<TableRow
					key="unread-header"
					style={{ background: "rgba(249, 249, 249, 0.50)" }}
				>
					<TableCell
						colSpan={isAdmin ? 5 : 4}
						style={{ padding: "16px", borderBottom: "1px solid #EAEAEA" }}
					>
						<Body2 text="Unread" fontType="semibold" />
					</TableCell>
				</TableRow>
			);

			// Add unread messages
			unreadMessages.forEach((row, index) => {
				renderedRows.push(
					<MessageRow
						row={row}
						key={`unread-${index}`}
						onMessageRowClick={onMessageRowClick}
						isAdmin={isAdmin}
					/>
				);
			});
		}

		// Read section
		if (readMessages.length > 0) {
			// Section header
			renderedRows.push(
				<TableRow
					key="read-header"
					style={{ background: "rgba(249, 249, 249, 0.50)" }}
				>
					<TableCell
						colSpan={isAdmin ? 5 : 4}
						style={{ padding: "16px", borderBottom: "1px solid #EAEAEA" }}
					>
						<Body2 text="Read" fontType="semibold" />
					</TableCell>
				</TableRow>
			);

			// Add read messages
			readMessages.forEach((row, index) => {
				renderedRows.push(
					<MessageRow
						row={row}
						key={`read-${index}`}
						onMessageRowClick={onMessageRowClick}
						isAdmin={isAdmin}
					/>
				);
			});
		}

		return renderedRows;
	};

	const [order, setOrder] = useState("desc");
	const [orderBy, setOrderBy] = useState();

	useEffect(() => {
		if (isAdmin && tableType === "messages") {
			setOrderBy("date-created");
		}
	}, [isAdmin, tableType]);

	const [filteredLoans, setFilteredLoans] = useState(rowData);

	const [minimizedRow, setMinimizedRow] = useState([]);
	const [activeDocumentTrackerRowId, setActiveDocumentTrackerRowId] =
		useState(null);
	const [activeEnhancedConditionRowId, setActiveEnhancedConditionRowId] =
		useState(null);

	const handleAddMinimizeRow = (rowId) => {
		setMinimizedRow((prevArray) => [...prevArray, rowId]);
	};

	const handleDeleteMinimizeRow = (rowId) => {
		setMinimizedRow((prevArray) => prevArray.filter((item) => item !== rowId));
	};

	useEffect(() => {
		// if (
		// 	tableType !== "pipeline" &&
		// 	tableType !== "admin-pipeline" &&
		// 	filteredLoans?.length === 0
		// ) {
		console.log("120 row data:", rowData);
		setFilteredLoans(rowData);
		// }
	}, [rowData]);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	// TODO uncomment to enable pagination
	// const [page, setPage] = useState(0);
	// const [rowsPerPage, setRowsPerPage] = useState(10);

	// const handleChangePage = (event, newPage) => {
	// 	setPage(newPage);
	// };

	// const handleChangeRowsPerPage = (event) => {
	// 	setRowsPerPage(+event.target.value);
	// 	setPage(0);
	// };

	const handleSelectAllClick = (event) => {
		if (event.target.checked) {
			const newSelected = rowData?.map((n) => n.id);
			setSelectedRow(newSelected);
			return;
		}
		setSelectedRow([]);
	};

	const getDocumentTrackerRowId = (row) => row?.entityId ?? row?.id;
	const selectedRowIds = Array.isArray(selectedRow) ? selectedRow : [];

	const selectedDocumentRows = rowData?.filter((row) =>
		selectedRowIds.includes(getDocumentTrackerRowId(row))
	);

	const sortedDocumentTrackerRows = useMemo(() => {
		if (tableType !== "broker-documents-tracker") {
			return filteredLoans ?? [];
		}

		const rowsToSort = Array.isArray(filteredLoans) ? [...filteredLoans] : [];
		const documentTrackerSortConfigMap = {
			"document-name": {
				field: "__trackerSortTitle",
				sort: sortAmount,
			},
			"upload-date": {
				field: "__trackerSortUploadedAt",
				sort: sortDate,
			},
			"user-name": {
				field: "__trackerSortUploadedBy",
				sort: sortAmount,
			},
			"logan-status": {
				field: "__trackerSortLoganStatus",
				sort: sortAmount,
			},
			"encompass-doc": {
				field: "__trackerSortEncompassDoc",
				sort: sortAmount,
			},
			"pipeline": {
				field: "__trackerSortPipelineStatus",
				sort: sortAmount,
			},
			"condition-type": {
				field: "__trackerSortMatchingConditionCount",
				sort: sortNumber,
			},
		};

		const sortConfig = documentTrackerSortConfigMap[orderBy];

		if (!sortConfig) {
			return rowsToSort;
		}

		const rowsWithSortValues = rowsToSort.map((row) => {
			const {
				matchingConditionCount,
				hasMatchingAttachment,
				isIndexed,
				indexedRecordTitle,
				isXpanseProcessingRecord,
			} = getTrackerMatchingConditionInfo(row, conditionDocuments);

			const hasAssignedTo = Array.isArray(row?.assignedTo)
				? row.assignedTo.length > 0
				: Boolean(row?.assignedTo);

			const pipelinePresentation = getDocumentPipelineStatusPresentation({
				row,
				isIndexed,
				indexedRecordTitle,
				isXpanseProcessingRecord,
				matchingConditionCount,
				hasMatchingAttachment,
				hasAssignedTo,
				isPendingRetryRecord: Boolean(row?.__isPendingRetryRecord),
			});
			const uploadedByDisplayText = getUploadedByDisplayText(row) ?? "";

			return {
				...row,
				__trackerSortTitle:
					row?.documentName ??
					row?.title ??
					row?.entityName ??
					row?.fileName ??
					row?.name ??
					"",
				__trackerSortUploadedAt:
					row?.updatedDate ?? row?.createdDate ?? "",
				__trackerSortUploadedBy: uploadedByDisplayText,
				__trackerSortLoganStatus:
					pipelinePresentation?.loganStatusText ?? "",
				__trackerSortEncompassDoc:
					pipelinePresentation?.encompassDocStatusText ?? "",
				__trackerSortPipelineStatus:
					pipelinePresentation?.pipelineStatusText ?? "",
				__trackerSortMatchingConditionCount: Number(
					matchingConditionCount ?? 0
				),
			};
		});

		if (sortConfig.sort === sortDate) {
			return sortDate(rowsWithSortValues, order, sortConfig.field);
		}

		return sortConfig.sort(rowsWithSortValues, order, sortConfig.field);
	}, [conditionDocuments, filteredLoans, order, orderBy, tableType]);

	const visibleDocumentTrackerRows = useMemo(() => {
		if (tableType !== "broker-documents-tracker") {
			return [];
		}

		return sortedDocumentTrackerRows ?? [];
	}, [sortedDocumentTrackerRows, tableType]);

	const visibleDocumentTrackerSelectedCount = useMemo(() => {
		if (tableType !== "broker-documents-tracker") {
			return 0;
		}

		const selectedIdSet = new Set(
			(Array.isArray(selectedDocumentIds) ? selectedDocumentIds : []).map(
				(value) => String(value)
			)
		);

		return visibleDocumentTrackerRows.filter((row) =>
			selectedIdSet.has(String(getDocumentTrackerRowId(row)))
		).length;
	}, [selectedDocumentIds, tableType, visibleDocumentTrackerRows]);

	const showDocumentTrackerConditionMeta = Boolean(
		rowData?.some((row) => row?.category || row?.internalDescription)
	);

	const handleSelectAllDocsClick = (event) => {
		if (tableType === "broker-documents-tracker") {
			onToggleAllDocumentSelection?.(
				visibleDocumentTrackerRows,
				event.target.checked
			);
			return;
		}

		if (event.target.checked) {
			const newSelected = rowData
				?.map(getDocumentTrackerRowId)
				?.filter((value) => value !== undefined && value !== null);
			setSelectedRow(newSelected);
			return;
		}
		setSelectedRow([]);
	};

	const handleSelectRowClick = (event, rowId) => {
		const selectedIndex = selectedRowIds.indexOf(rowId);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedRowIds, rowId);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedRowIds.slice(1));
		} else if (selectedIndex === selectedRowIds.length - 1) {
			newSelected = newSelected.concat(selectedRowIds.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedRowIds.slice(0, selectedIndex),
				selectedRowIds.slice(selectedIndex + 1)
			);
		}

		setSelectedRow(newSelected);
	};

	const handleSelectEligibleRowClick = (event, productData) => {
		console.log("150 product data:", productData);
		console.log("150 currentProductName:", currentProductName);

		setSelectedRow(productData);
		setSelectedProduct(currentProductName);
	};

	const customClass = `${styles.tablePaperContainer} ${
		squaredBorder ? styles.squaredBorderContainer : ""
	} ${maxHeight ? styles.maxHeight : ""}`;

	const renderEmptyTableState = (message = "No conditions found") => (
		<TableRow className={styles.emptyTableRow}>
			<TableCell
				colSpan={columnData?.length || 1}
				className={styles.emptyTableCell}
			>
				<Box className={styles.emptyTableState}>
					<Body2
						text={message}
						extraClass={styles.emptyTableStateText}
						fontType="semibold"
					/>
				</Box>
			</TableCell>
		</TableRow>
	);

	return (
		<Paper sx={{ width: "100%", overflow: "hidden" }} className={customClass}>
			<TableContainer className={styles.tableContainer}>
				<Table stickyHeader aria-label="sticky table" className={styles.table}>
					<TableHead data={columnData} />
					{tableType === "condition-tracking" ? (
						<TableHeadConditionTracking
							data={columnData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							filteredLoans={filteredLoans}
							setFilteredLoans={setFilteredLoans}
						/>
					) : tableType === "enhanced-condition-tracking" ? (
						<TableHeadEnhancedConditionTracking
							data={columnData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							filteredLoans={filteredLoans}
							setFilteredLoans={setFilteredLoans}
						/>
					) : tableType === "document-tracker-conditions" ? (
						<TableHeadDocumentTrackerConditions
							rowCount={rowData.length}
							selectedCount={selectedRowIds.length}
							onSelectAllClick={handleSelectAllDocsClick}
							showMetaColumns={showDocumentTrackerConditionMeta}
						/>
					) : tableType === "satisfying-conditions" ? (
						<TableHeadSatisfyCondition
							data={columnData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							filteredLoans={filteredLoans}
							setFilteredLoans={setFilteredLoans}
						/>
					) : tableType === "resources-file" ? (
						<TableHeadResourceFile
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							filteredLoans={filteredLoans}
							setFilteredLoans={setFilteredLoans}
						/>
					) : tableType === "eligible-product" ? (
						<TableHeadEligibleProduct
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
						/>
					) : tableType === "ineligible-product" ? (
						<TableHeadIneligibleProduct
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
						/>
					) : tableType === "pipeline" || tableType === "admin-pipeline" ? (
						<TableHeadPipeline
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							filteredLoans={filteredLoans}
							setFilteredLoans={setFilteredLoans}
						/>
					) : tableType === "loan-documents" ? (
						<TableHeadLoanDocuments
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							selectedCount={selectedRowIds.length}
							onSelectAllClick={handleSelectAllDocsClick}
						/>
					) : tableType === "broker-documents-tracker" ? (
					<TableHeadLoanDocumentsTracker
						data={columnData}
						rowData={rowData}
						order={order}
						orderBy={orderBy}
						onRequestSort={handleRequestSort}
						rowCount={visibleDocumentTrackerRows.length}
						filteredLoans={filteredLoans}
						setSortedData={setSortedData}
						setFilteredLoans={setFilteredLoans}
						selectedCount={visibleDocumentTrackerSelectedCount}
						onSelectAllClick={handleSelectAllDocsClick}
					/>
					) : tableType === "messages" ? (
						<TableHeadMessages
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							setSortedData={setSortedData}
						/>
					) : enableSelect ? (
						<TableHeadCheckboxSort
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData.length}
							setSortedData={setSortedData}
							selectedCount={selectedRowIds.length}
							onSelectAllClick={handleSelectAllClick}
						/>
					) : enableSort ? (
						<TableHeadSort
							data={columnData}
							rowData={rowData}
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							rowCount={rowData?.length}
							setSortedData={setSortedData}
						/>
					) : (
						<CustomTableHead data={columnData} />
					)}

					{/* TODO uncomment to enable loader */}
					{/* {!isLoading ? (
						<TableBody
							sx={{
								backgroundColor: "red",
							}}
						>
							<TableRow>
								<TableCell align="center">
									<CustomLoader />
								</TableCell>
							</TableRow>
						</TableBody>
					) : ( */}

					<TableBody>
						{tableType === "admin-pipeline" && filteredLoans?.length > 0
							? filteredLoans?.map((row, index) => (
									<AdminPipelineRow
										row={row}
										key={index}
										handleOpenConfirmModal={handleOpenConfirmModal}
									/>
								))
							: tableType === "pipeline" && filteredLoans?.length > 0
								? filteredLoans?.map((row, index) => (
										<PipelineRow row={row} key={index} />
									))
								: tableType === "condition-tracking" &&
									  filteredLoans?.length > 0
									? filteredLoans?.map((row, index) => (
											<ConditionTrackingRow
												key={index}
												row={row}
												handleUploadClick={handleConditionUploadClick}
												handleDownloadClick={handleConditionDownloadClick}
												minimizedRow={minimizedRow}
												handleAddMinimizeRow={handleAddMinimizeRow}
												handleDeleteMinimizeRow={handleDeleteMinimizeRow}
											/>
										))
									: tableType === "enhanced-condition-tracking"
										? filteredLoans?.length > 0
											? filteredLoans?.map((row, index) => (
													<EnhancedConditionTrackingRow
														key={index}
														row={row}
														setDocumentsList={setDocumentsList}
														handleUploadClick={handleConditionUploadClick}
														handleDownloadClick={handleConditionDownloadClick}
														toggleDrawer={toggleDrawer}
														loanId={loanId}
														extraData={extraData}
														minimizedRow={minimizedRow}
														handleAddMinimizeRow={handleAddMinimizeRow}
														handleDeleteMinimizeRow={handleDeleteMinimizeRow}
														activeRowId={activeEnhancedConditionRowId}
														setActiveRowId={
															setActiveEnhancedConditionRowId
														}
													/>
												))
											: renderEmptyTableState()
										: tableType === "document-tracker-conditions" &&
											  filteredLoans?.length > 0
											? filteredLoans?.map((row, index) => (
													<DocumentTrackerConditionsRow
														key={row?.id ?? row?.entityId ?? index}
														row={row}
														handleRowClick={handleSelectRowClick}
														selectedRow={selectedRow}
														showMetaColumns={showDocumentTrackerConditionMeta}
													/>
												))
											: tableType === "satisfying-conditions" &&
												  filteredLoans?.length > 0
												? filteredLoans?.map((row, index) => (
														<SatisfyingConditionsRow
															key={index}
															row={row}
															handleRowClick={handleSelectRowClick}
															selectedRow={selectedRow}
															minimizedRow={minimizedRow}
															handleAddMinimizeRow={handleAddMinimizeRow}
															handleDeleteMinimizeRow={handleDeleteMinimizeRow}
														/>
													))
												: tableType === "resources-file" &&
													  filteredLoans?.length > 0
													? filteredLoans?.map((row, index) => (
															<ResourceFileRow
																key={index}
																row={row}
																index={index}
																// resources file
																handleFileRename={handleFileRename}
																handleFileDelete={handleFileDelete}
															/>
														))
													: tableType === "broker-documents-tracker" &&
														  sortedDocumentTrackerRows?.length > 0
														? sortedDocumentTrackerRows?.map((row, index) => (
																<ConditionsDocumentTrackerRow
																	key={
																		row?.entityId ??
																		row?.id ??
																		row?.uploadKey ??
																		index
																	}
																	row={row}
																	index={index}
																	selectedRow={selectedRow}
																	selectedDocumentIds={selectedDocumentIds}
																	onToggleDocumentSelection={
																		onToggleDocumentSelection
																	}
																	activeRowId={
																		activeDocumentTrackerRowId
																	}
																	setActiveRowId={
																		setActiveDocumentTrackerRowId
																	}
																	toggleDrawer={toggleDrawer}
																	loanId={loanId}
																	conditionTypeOptions={conditionTypeOptions}
																	setConditionsOptions={setConditionsOptions}
																	isConditionStatusLoaded={
																		isConditionStatusLoaded
																	}
																	selectedConditionTypes={
																		selectedConditionTypes
																	}
																	setSelectedConditionTypes={
																		setSelectedConditionTypes
																	}
																	selectedDocuments={selectedDocumentRows}
																	conditionDocuments={conditionDocuments}
																	onRefreshDocumentsList={
																		onRefreshDocumentsList
																	}
																	onViewDocument={onViewDocument}
																	onDownloadDocument={onDownloadDocument}
																	// resources file
																	handleFileRename={handleFileRename}
																	handleFileDelete={handleFileDelete}
																/>
															))
														: tableType === "messages"
															? renderMessagesWithSections()
															: rowData
																	// TODO uncomment to enable pagination
																	// .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
																	?.map((row, index) =>
																		tableType === "loan-status" ? (
																			<LoanStatusRow row={row} key={index} />
																		) : tableType === "rate-lock" ? (
																			<RateLockRow row={row} key={index} />
																		) : tableType === "lock-history" ? (
																			<LockHistoryRow row={row} key={index} />
																		) : tableType === "change-request" ? (
																			<ChangeRequestRow row={row} key={index} />
																		) : tableType === "broker-documents" ? (
																			<BrokerDocumentRow
																				row={row}
																				key={index}
																				handleRowClick={handleSelectRowClick}
																				selectedRow={selectedRow}
																			/>
																		) : tableType === "loan-documents" ? (
																			<LoanDocumentRow
																				row={row}
																				key={index}
																				handleRowClick={handleSelectRowClick}
																				selectedRow={selectedRow}
																				onViewDocument={onViewDocument}
																				onDownloadDocument={onDownloadDocument}
																			/>
																		) : tableType === "additional-income" ? (
																			<AdditionalIncomeRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																			/>
																		) : tableType === "asset-information" ? (
																			<AssetInformationRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																			/>
																		) : tableType ===
																		  "liabilities-information" ? (
																			<LiabilityInformationRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																				isSubmitted={isSubmitted}
																			/>
																		) : tableType ===
																		  "real-estate-information" ? (
																			<RealEstateInformationRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																				liabilityCount={liabilityCount}
																				handleReOwned={handleReOwned}
																				handleViewLinked={handleViewLinked}
																				isSubmitted={isSubmitted}
																			/>
																		) : tableType === "resources-folder" ? (
																			<ResourceFolderRow
																				key={index}
																				row={row}
																				index={index}
																				// resources folder
																				handleFolderClick={handleFolderClick}
																				handleFolderDelete={handleFolderDelete}
																			/>
																		) : tableType === "admin-users" ? (
																			<AdminUserRow
																				key={index}
																				row={row}
																				index={index}
																				selectedBroker={selectedBroker}
																				handleSelectBroker={handleSelectBroker}
																			/>
																		) : tableType === "link-liability" ? (
																			<LinkLiabilityRow
																				key={index}
																				row={row}
																				index={index}
																				selectedRealEstate={selectedRealEstate}
																				handleSelectLiability={
																					handleSelectLiability
																				}
																			/>
																		) : tableType === "appraisal-documents" ? (
																			<AppraisalDocumentsRow
																				key={index}
																				row={row}
																				handleDownload={handleDownload}
																			/>
																		) : tableType ===
																		  "other-assets-information" ? (
																			<OtherAssetsInformationRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																			/>
																		) : tableType ===
																		  "other-liabilities-information" ? (
																			<OtherLiabilitiesInformationRow
																				key={index}
																				row={row}
																				index={index}
																				handleRemoveRow={handleRemoveRow}
																				handleUpdate={handleUpdate}
																				disableRow={disableRow}
																			/>
																		) : tableType === "eligible-product" ? (
																			<EligibleProductRow
																				row={row}
																				key={index}
																				handleRowClick={
																					handleSelectEligibleRowClick
																				}
																				selectedRow={selectedRow}
																				toggleDrawer={toggleDrawer}
																				selectedProduct={selectedProduct}
																				hidePriceColumn={hidePriceColumn}
																				hidePointsPercentageColumn={
																					hidePointsPercentageColumn
																				}
																				hidePointsValueColumn={
																					hidePointsValueColumn
																				}
																			/>
																		) : tableType ===
																		  "eligible-product-adjustments" ? (
																			<EligibleProductAdjustmentsRow
																				key={index}
																				id={index}
																				row={row}
																				rowData={rowData}
																				extraData={extraData}
																				hideAdjustmentPriceColumn={
																					hideAdjustmentPriceColumn
																				}
																			/>
																		) : tableType ===
																		  "eligible-product-payments" ? (
																			<EligibleProductPaymentsRow
																				row={row}
																				key={index}
																			/>
																		) : (
																			tableType === "ineligible-product" && (
																				<IneligibleProductRow
																					row={row}
																					key={index}
																					handleRowClick={
																						handleSelectEligibleRowClick
																					}
																					selectedRow={selectedRow}
																				/>
																			)
																		)
																	)}
					</TableBody>
					{/* )} */}
				</Table>
			</TableContainer>

			{/* TODO uncomment to enable pagination */}
			{/* <TablePagination
				rowsPerPageOptions={[10, 25, 100]}
				component="div"
				count={rows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/> */}
		</Paper>
	);
};

export default CustomTable;
